/**
 * ID front + back upload widget, designed to drop into the onboarding
 * form factory as a COMPONENT field.
 *
 * State lives in the parent form (form.id_front + form.id_back); the
 * uploader writes File objects there. The onboard onSubmit detects
 * File instances and builds a FormData payload.
 *
 * When the user is already verified, the widget hides itself behind a
 * "verified" notice — the lock is also enforced server-side.
 */

import { useState } from "react";
import { Alert, Button, Modal, Upload } from "antd";
import {
  CheckmarkCircleOutline,
  CloudUploadOutline,
  EyeOutline,
} from "@raresail/react-ionicons";
import { useAuth } from "../context/AuthContext";
import { resolveMediaUrl } from "../../constants/config";

interface Props {
  form: any;
  updateFormValues: (v: any) => void;
}

export const IDDocumentsUpload = ({ form, updateFormValues }: Props) => {
  const { user } = useAuth();
  const isVerified = !!user?.is_verified;

  const hasFrontOnFile = !!(form?.id_front || user?.id_front);
  const hasBackOnFile = !!(form?.id_back || user?.id_back);

  const isFront = form?.id_front instanceof File;
  const isBack = form?.id_back instanceof File;

  // Lightbox state — users can preview their own uploaded IDs.
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(
    null,
  );

  // What URL to preview for each side: if the form has a fresh File,
  // show that via an object URL; otherwise fall back to whatever the
  // server already has on file.
  const previewUrl = (side: "front" | "back"): string | null => {
    const formFile = side === "front" ? form?.id_front : form?.id_back;
    if (formFile instanceof File) return URL.createObjectURL(formFile);
    const stored = side === "front" ? user?.id_front : user?.id_back;
    return stored ? resolveMediaUrl(stored) : null;
  };

  if (isVerified) {
    return (
      <div
        className="flexNullCenter gap10 p15 r10"
        style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}
      >
        <CheckmarkCircleOutline color="#15803d" fontSize={22} />
        <div className="flexColumn gap5">
          <div className="font13 semiBold" style={{ color: "#15803d" }}>
            Identity verified
          </div>
          <div className="font12" style={{ color: "#166534", lineHeight: 1.4 }}>
            Your ID documents are locked. Contact support to update them.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flexColumn gap15">
      <div className="font12 grey" style={{ lineHeight: 1.5 }}>
        Upload a clear photo of the <b>front</b> and <b>back</b> of your
        national ID, passport, or driver's licence. We use these to verify
        your identity before you can borrow or withdraw.
      </div>

      <div className="flexColumn gap5">
        <div className="flexSpaceCenter">
          <div className="font12 textDark semiBold">
            ID Front
            {hasFrontOnFile && !isFront && (
              <span className="font11 grey ml5"> (uploaded — replace optional)</span>
            )}
          </div>
          {hasFrontOnFile && (
            <Button
              type="link"
              size="small"
              icon={<EyeOutline />}
              onClick={() => {
                const u = previewUrl("front");
                if (u) setPreview({ url: u, title: "ID Front" });
              }}
            >
              Preview
            </Button>
          )}
        </div>
        <Upload
          beforeUpload={(file) => {
            updateFormValues({ id_front: file });
            return false; // we send via PATCH ourselves
          }}
          onRemove={() => updateFormValues({ id_front: null })}
          accept="image/*"
          maxCount={1}
          fileList={
            isFront
              ? [
                  {
                    uid: "front",
                    name: (form.id_front as File).name,
                    status: "done",
                  } as any,
                ]
              : []
          }
        >
          <Button icon={<CloudUploadOutline />} block size="large">
            {isFront ? "Change file" : "Choose file"}
          </Button>
        </Upload>
      </div>

      <div className="flexColumn gap5">
        <div className="flexSpaceCenter">
          <div className="font12 textDark semiBold">
            ID Back
            {hasBackOnFile && !isBack && (
              <span className="font11 grey ml5"> (uploaded — replace optional)</span>
            )}
          </div>
          {hasBackOnFile && (
            <Button
              type="link"
              size="small"
              icon={<EyeOutline />}
              onClick={() => {
                const u = previewUrl("back");
                if (u) setPreview({ url: u, title: "ID Back" });
              }}
            >
              Preview
            </Button>
          )}
        </div>
        <Upload
          beforeUpload={(file) => {
            updateFormValues({ id_back: file });
            return false;
          }}
          onRemove={() => updateFormValues({ id_back: null })}
          accept="image/*"
          maxCount={1}
          fileList={
            isBack
              ? [
                  {
                    uid: "back",
                    name: (form.id_back as File).name,
                    status: "done",
                  } as any,
                ]
              : []
          }
        >
          <Button icon={<CloudUploadOutline />} block size="large">
            {isBack ? "Change file" : "Choose file"}
          </Button>
        </Upload>
      </div>

      <Modal
        open={!!preview}
        onCancel={() => setPreview(null)}
        title={preview?.title}
        footer={null}
        width={520}
        destroyOnClose
      >
        {preview && (
          <img
            src={preview.url}
            alt={preview.title}
            style={{
              width: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
              borderRadius: 8,
            }}
          />
        )}
      </Modal>

      {(hasFrontOnFile || hasBackOnFile) && (
        <Alert
          type="info"
          showIcon
          message={
            hasFrontOnFile && hasBackOnFile
              ? "Both IDs on file — they'll be reviewed by an admin."
              : "Upload both sides to complete your KYC."
          }
        />
      )}
    </div>
  );
};

export default IDDocumentsUpload;
